import React, { useEffect, useMemo } from 'react'
import { Form } from 'antd'
import { useStore, useUnit } from 'effector-react'
import dayjs, { Dayjs } from 'dayjs'
import {
  $transferForm,
  addTransfer,
  addTransferMounted,
  deleteTransfer,
  deleteTransferMounted,
  editTransferMounted,
  editTransferPayment,
  getTransferById,
  getTransferByIdMounted,
  setTransferForm,
} from './model'
import { $userProfile } from '../../../../../features/user'
import { transactionType } from '../../../../../libs/finSystem'
import {
  formatTransactionDateTime,
  parseDecimal,
  parseNaiveDateTime,
} from '../../../../../libs/utils'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { $wallet as $currentWallet } from '../../details/model'
import { initialTransferModalState } from './const'
import { ModalType } from '../types'
import { TransactionModalLayout } from '../TransactionModalLayout'
import { useTransactionForm } from '../useTransactionForm'
import {
  AmountSentReceivedRow,
  CommentField,
  CommissionRow,
  DateTimeRow,
  ExchangeRateRow,
  LockedTypeSelect,
  WalletSelect,
  useDefaultCurrencyOnce,
} from '../transactionFields'

type Props = {
  isOpen: boolean
  mode?: 'create' | 'edit'
  transactionId?: string
  onClose: () => void
  onCancel?: () => void
  type: ModalType
}

type FormValues = {
  fromWallet: string
  toWalletId: string
  sentAmount: string
  sentCurrency: string
  receivedAmount: string
  receivedCurrency: string
  sentCommission: string
  sentCommissionCurrency: string
  receivedCommission: string
  receivedCommissionCurrency: string
  sentExchangeRate: string
  receivedExchangeRate: string
  date: Dayjs | null
  time: Dayjs | null
  inProgress: boolean
  comment: string
}

const calcRate = (top: string, bottom: string): string => {
  const t = parseDecimal(top)
  const b = parseDecimal(bottom)
  if (!b) return '0'
  const r = t / b
  return Number.isFinite(r) ? String(r) : '0'
}

export const TransferTransactionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCancel,
  mode = 'create',
  transactionId,
  type,
}) => {
  const [form] = Form.useForm<FormValues>()

  const allWallets = useUnit($walletAllowedList)
  const userProfile = useStore($userProfile)
  const currentWallet = useStore($currentWallet)

  const fromWallet = Form.useWatch('fromWallet', form)
  const toWalletId = Form.useWatch('toWalletId', form)
  const sentAmount = Form.useWatch('sentAmount', form)
  const receivedAmount = Form.useWatch('receivedAmount', form)
  const sentCurrency = Form.useWatch('sentCurrency', form)
  const receivedCurrency = Form.useWatch('receivedCurrency', form)

  useDefaultCurrencyOnce(form, 'sentCurrency', 'sentCommissionCurrency')
  useDefaultCurrencyOnce(form, 'receivedCurrency', 'receivedCommissionCurrency')

  const fromWalletCurrencies = useMemo(() => {
    if (currentWallet.id) {
      return currentWallet.localBalances?.map((item) => item.currency) ?? []
    }
    const w = allWallets.find((item) => Number(item.id) === Number(fromWallet))
    return w?.currencies ?? []
  }, [allWallets, fromWallet, currentWallet])

  const toWalletCurrencies = useMemo(() => {
    const w = allWallets.find((item) => String(item.id) === String(toWalletId))
    return w?.currencies ?? []
  }, [allWallets, toWalletId])

  useEffect(() => {
    if (sentAmount && receivedAmount) {
      form.setFieldsValue({
        sentExchangeRate: calcRate(sentAmount, receivedAmount),
        receivedExchangeRate: calcRate(receivedAmount, sentAmount),
      })
    }
  }, [sentAmount, receivedAmount, form])

  const {
    storeData: transferForm,
    handleSubmit,
    handleSubmitAndCreateNew,
    handleCancel,
    handleDelete,
    isSubmitting,
    isDeleting,
    isLoadingForm,
  } = useTransactionForm({
    form,
    mode,
    transactionId,
    isOpen,
    onClose,
    onCancel,
    store: $transferForm,
    initialState: initialTransferModalState,
    setForm: setTransferForm,
    mapStoreToForm: (data) => {
      const dt = parseNaiveDateTime(data.dateTime)
      return {
        fromWallet: data.fromWallet,
        toWalletId: data.toWalletId,
        sentAmount: String(data.sentAmount ?? ''),
        sentCurrency: data.sentCurrency,
        receivedAmount: String(data.receivedAmount ?? ''),
        receivedCurrency: data.receivedCurrency,
        sentCommission: String(data.sentCommission ?? '0'),
        sentCommissionCurrency: data.sentCommissionCurrency,
        receivedCommission: String(data.receivedCommission ?? '0'),
        receivedCommissionCurrency: data.receivedCommissionCurrency,
        sentExchangeRate: String(data.sentExchangeRate ?? '0'),
        receivedExchangeRate: String(data.receivedExchangeRate ?? '0'),
        date: dt ? dayjs(dt.date) : dayjs(),
        time: dt ? dayjs(dt.time, 'HH:mm:ss') : dayjs(),
        inProgress: data.inProgress,
        comment: data.comment,
      }
    },
    events: {
      addMount: addTransferMounted,
      editMount: editTransferMounted,
      deleteMount: deleteTransferMounted,
      getByIdMount: getTransferByIdMounted,
    },
    effects: {
      add: addTransfer,
      edit: editTransferPayment,
      delete: deleteTransfer,
      getById: getTransferById,
    },
    buildPayload: (values, { mode: m, transactionId: tid }) => ({
      id: m === 'edit' ? tid : '',
      fromWallet:
        values.fromWallet ||
        (currentWallet.id ? String(currentWallet.id) : ''),
      toWalletId: values.toWalletId,
      toWallet: values.toWalletId,
      sentAmount: values.sentAmount,
      sentCurrency: values.sentCurrency,
      receivedAmount: values.receivedAmount,
      receivedCurrency: values.receivedCurrency,
      sentCommission: values.sentCommission || '0',
      sentCommissionCurrency: values.sentCommissionCurrency,
      receivedCommission: values.receivedCommission || '0',
      receivedCommissionCurrency: values.receivedCommissionCurrency,
      sentExchangeRate: values.sentExchangeRate || '0',
      receivedExchangeRate: values.receivedExchangeRate || '0',
      comment: values.comment ?? '',
      inProgress: !!values.inProgress,
      dateTime: formatTransactionDateTime(
        values.date!.format('YYYY-MM-DD'),
        values.time!.format('HH:mm:ss'),
      ),
    }),
  })

  return (
    <TransactionModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      title={`${mode === 'edit' ? 'Edit' : 'Create'} transfer${transferForm.id ? ` #${transferForm.id}` : ''}`}
      mode={mode}
      creatorEmail={userProfile.email}
      isLoading={isLoadingForm}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      onSubmit={handleSubmit}
      onSubmitAndCreateNew={
        mode === 'create' ? handleSubmitAndCreateNew : undefined
      }
      onDelete={mode === 'edit' ? handleDelete : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          fromWallet:
            type === 'wallet' && currentWallet.id
              ? String(currentWallet.id)
              : '',
          sentCommission: '0',
          receivedCommission: '0',
          sentExchangeRate: '0',
          receivedExchangeRate: '0',
          inProgress: false,
          date: dayjs(),
          time: dayjs(),
        }}
        requiredMark={false}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <WalletSelect
            name="fromWallet"
            label="From"
            required
            disabled={type === 'wallet' || mode === 'edit'}
            style={{ flex: 1 }}
          />
          <LockedTypeSelect
            value={transactionType[0].value}
            options={transactionType}
            style={{ flex: 1 }}
          />
        </div>

        <WalletSelect name="toWalletId" label="To" required />

        <AmountSentReceivedRow
          sentAmountName="sentAmount"
          sentCurrencyName="sentCurrency"
          sentCurrencies={fromWalletCurrencies}
          sentRequired
          sentCurrencyRequired
          receivedAmountName="receivedAmount"
          receivedCurrencyName="receivedCurrency"
          receivedCurrencies={toWalletCurrencies}
          receivedRequired
          receivedCurrencyRequired
        />

        <CommissionRow
          sentName="sentCommission"
          sentCurrencyName="sentCommissionCurrency"
          sentCurrencies={fromWalletCurrencies}
          receivedName="receivedCommission"
          receivedCurrencyName="receivedCommissionCurrency"
          receivedCurrencies={toWalletCurrencies}
        />

        <ExchangeRateRow
          sentName="sentExchangeRate"
          sentLabel={`Sending exchanger rate ${sentCurrency || ''}${receivedCurrency || ''}`}
          receivedName="receivedExchangeRate"
          receivedLabel={`Acceptance exchanger rate ${receivedCurrency || ''}${sentCurrency || ''}`}
        />

        <DateTimeRow inProgressName="inProgress" />

        <CommentField />
      </Form>
    </TransactionModalLayout>
  )
}
